import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { isMobileTicket } from '@v2/utils';

/** High-level venue zone buckets for chips + ranking */
export type ZoneBucket =
  | 'field'
  | 'main'
  | 'terrace'
  | 'grandstand'
  | 'bleachers'
  | 'suite'
  | 'other';

export const ZONE_BUCKET_LABELS: Record<ZoneBucket, string> = {
  field: 'Field level',
  main: 'Main level',
  terrace: 'Terrace',
  grandstand: 'Grandstand',
  bleachers: 'Bleachers',
  suite: 'Suites / Club',
  other: 'Other',
};

/** Higher = better location for "Best location" sort */
const ZONE_LOCATION_WEIGHT: Record<ZoneBucket, number> = {
  field: 100,
  main: 80,
  terrace: 60,
  suite: 70,
  bleachers: 45,
  grandstand: 40,
  other: 30,
};

const OBSTRUCTED_RE =
  /obstruct|limited\s*view|partial\s*view|pole|pillar|behind/i;
const ADA_RE = /\bADA\b|accessible|wheelchair|handicap/i;

export function getZoneKey(tevoSectionName?: string | null): string {
  if (!tevoSectionName) return 'unknown';
  return (
    tevoSectionName
      .toLowerCase()
      .replace(/\s+\d+[a-z]?\s*$/i, '')
      .replace(/\s+[a-z]$/i, '')
      .trim() || 'unknown'
  );
}

export function getZoneBucket(tevoSectionName?: string | null): ZoneBucket {
  const name = (tevoSectionName || '').toLowerCase();
  if (!name) return 'other';

  // Order matters: "grandstand outfield" contains "field" as a substring.
  if (/\b(suite|club|legends|delta|jim beam|champions|lounge)\b/.test(name)) {
    return 'suite';
  }
  if (/\bgrandstand\b|upper|mezzanine|balcony/.test(name)) return 'grandstand';
  if (/\bbleacher/.test(name)) return 'bleachers';
  if (/\bterrace\b/.test(name)) return 'terrace';
  if (/\bmain\b/.test(name)) return 'main';
  // Field level (includes field mvp / field infield / field outfield)
  if (/\bfield\b/.test(name)) return 'field';
  return 'other';
}

export function parseRowNumber(row?: string | null): number | null {
  if (!row) return null;
  const match = String(row).match(/(\d+)/);
  return match ? Number(match[1]) : null;
}

export function isObstructedView(notes?: string | null): boolean {
  if (!notes) return false;
  return OBSTRUCTED_RE.test(notes);
}

export function isAccessibleListing(ticket: TicketGroup): boolean {
  if (ticket.wheelchair) return true;
  return ADA_RE.test(ticket.public_notes || '');
}

export function isElectronicListing(ticket: TicketGroup): boolean {
  return isMobileTicket(ticket);
}

export { isMobileTicket };

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

export type ZonePriceStats = Map<string, number>;

export function buildZoneMedianPrices(tickets: TicketGroup[]): ZonePriceStats {
  const byZone = new Map<string, number[]>();
  for (const t of tickets) {
    const key = getZoneKey(t.tevo_section_name);
    const list = byZone.get(key) || [];
    list.push(t.retail_price);
    byZone.set(key, list);
  }
  const stats: ZonePriceStats = new Map();
  byZone.forEach((prices, key) => {
    stats.set(key, median(prices));
  });
  return stats;
}

/**
 * Relative value within this event's inventory (not a market deal score).
 * Higher is better.
 */
export function getValueScore(
  ticket: TicketGroup,
  zoneMedians: ZonePriceStats
): number {
  const zoneKey = getZoneKey(ticket.tevo_section_name);
  const zoneMedian = zoneMedians.get(zoneKey) || ticket.retail_price || 1;
  const price = Math.max(ticket.retail_price, 1);
  const priceRatio = zoneMedian / price;

  const bucket = getZoneBucket(ticket.tevo_section_name);
  const locationWeight = ZONE_LOCATION_WEIGHT[bucket] / 100;

  const rowNum = parseRowNumber(ticket.row);
  // Lower rows slightly preferred; cap influence
  const rowWeight =
    rowNum == null ? 1 : Math.max(0.85, 1.08 - Math.min(rowNum, 30) * 0.008);

  const obstructionPenalty = isObstructedView(ticket.public_notes) ? 0.7 : 1;

  return (
    priceRatio * (0.55 + 0.45 * locationWeight) * rowWeight * obstructionPenalty
  );
}

export function getLocationScore(ticket: TicketGroup): number {
  const bucket = getZoneBucket(ticket.tevo_section_name);
  const base = ZONE_LOCATION_WEIGHT[bucket];
  const rowNum = parseRowNumber(ticket.row);
  const rowBonus = rowNum == null ? 0 : Math.max(0, 20 - Math.min(rowNum, 20));
  const obstructionPenalty = isObstructedView(ticket.public_notes) ? -25 : 0;
  // Prefer lower price as weak tie-breaker (subtract tiny amount)
  const priceTie = -ticket.retail_price / 100000;
  return base + rowBonus + obstructionPenalty + priceTie;
}

export function sortTickets(
  tickets: TicketGroup[],
  sortBy: 'lowestPrice' | 'bestValue' | 'bestLocation',
  zoneMedians?: ZonePriceStats
): TicketGroup[] {
  const list = [...tickets];
  const medians = zoneMedians || buildZoneMedianPrices(list);

  if (sortBy === 'lowestPrice') {
    return list.sort((a, b) => a.retail_price - b.retail_price);
  }

  if (sortBy === 'bestLocation') {
    return list.sort((a, b) => getLocationScore(b) - getLocationScore(a));
  }

  // bestValue
  return list.sort(
    (a, b) => getValueScore(b, medians) - getValueScore(a, medians)
  );
}

/** Top ~15% by value score, min 3 max 8, non-obstructed preferred */
export function getGreatDeals(
  tickets: TicketGroup[],
  zoneMedians?: ZonePriceStats,
  limit = 6
): TicketGroup[] {
  if (!tickets.length) return [];
  const medians = zoneMedians || buildZoneMedianPrices(tickets);
  const scored = tickets
    .filter((t) => !isObstructedView(t.public_notes))
    .map((t) => ({ t, score: getValueScore(t, medians) }))
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return [];

  const thresholdIndex = Math.max(0, Math.ceil(scored.length * 0.15) - 1);
  const cutoffScore = scored[Math.min(thresholdIndex, scored.length - 1)].score;

  const great = scored.filter((s) => s.score >= cutoffScore).map((s) => s.t);

  // Deduplicate by section+row so carousel isn't all same section
  const seen = new Set<string>();
  const unique: TicketGroup[] = [];
  for (const t of great) {
    const key = `${t.section}|${t.row}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(t);
    if (unique.length >= limit) break;
  }

  // Fallback: top N unique if threshold too strict
  if (unique.length < Math.min(3, scored.length)) {
    for (const { t } of scored) {
      const key = `${t.section}|${t.row}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(t);
      if (unique.length >= Math.min(3, limit)) break;
    }
  }

  return unique;
}

export function isGreatDealTicket(
  ticket: TicketGroup,
  greatDealIds: Set<number>
): boolean {
  return greatDealIds.has(ticket.id);
}

export function availableZoneBuckets(tickets: TicketGroup[]): ZoneBucket[] {
  const present = new Set<ZoneBucket>();
  tickets.forEach((t) => present.add(getZoneBucket(t.tevo_section_name)));
  const order: ZoneBucket[] = [
    'field',
    'main',
    'terrace',
    'bleachers',
    'grandstand',
    'suite',
    'other',
  ];
  return order.filter((z) => present.has(z) && z !== 'other');
}

export function percentilePrice(tickets: TicketGroup[], p: number): number {
  if (!tickets.length) return 0;
  const prices = tickets.map((t) => t.retail_price).sort((a, b) => a - b);
  const idx = Math.min(
    prices.length - 1,
    Math.max(0, Math.floor((prices.length - 1) * p))
  );
  return prices[idx];
}
